import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { BreadcrumbItemBase } from '../BreadcrumbItem/BreadcrumbItemBase.js';
import { ButtonMenu } from '../ButtonMenu/ButtonMenu.js';
import { IconMore } from '../Icons/IconMore.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["children"],
  _excluded2 = ["as", "label"],
  _excluded3 = ["aria-label", "children"];
var StyledBreadcrumb = styled.nav.withConfig({
  displayName: "Breadcrumb__StyledBreadcrumb",
  componentId: "sc-8lr3tb-0"
})(["&&{font-size:16px;}"]);
var StyledBreadcrumbList = styled.ol.withConfig({
  displayName: "Breadcrumb__StyledBreadcrumbList",
  componentId: "sc-8lr3tb-1"
})(["&&{margin:0;padding-left:0;list-style:none;display:flex;align-items:center;}"]);
var BreadcrumbItemsCollapsed = /*#__PURE__*/React.forwardRef(function BreadcrumbItemsCollapsed2(props, ref) {
  var children = props.children,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  return /*#__PURE__*/jsx(BreadcrumbItemBase, _objectSpread(_objectSpread({
    ref: ref,
    isFirst: true
  }, rest), {}, {
    children: /*#__PURE__*/jsx(ButtonMenu, {
      tooltipProps: {
        content: t("Breadcrumb.showPaths"),
        position: "bottom"
      },
      buttonProps: {
        hideLabel: true,
        label: t("Breadcrumb.collapsedItems"),
        startAdornment: IconMore,
        variant: "ghost"
      },
      options: React.Children.map(children, function (breadcrumbItem) {
        var _breadcrumbItem$props = breadcrumbItem.props,
          _breadcrumbItem$props2 = _breadcrumbItem$props.as,
          as = _breadcrumbItem$props2 === void 0 ? "a" : _breadcrumbItem$props2,
          label = _breadcrumbItem$props.label,
          itemProps = _objectWithoutProperties(_breadcrumbItem$props, _excluded2);
        return _objectSpread(_objectSpread({
          as: as,
          children: label
        }, itemProps), {}, {
          border: true
        });
      })
    })
  }));
});
BreadcrumbItemsCollapsed.displayName = "BreadcrumbItemsCollapsed";
var Breadcrumb = /*#__PURE__*/React.forwardRef(function Breadcrumb2(props, ref) {
  var _useThemeContext2 = useThemeContext(),
    localization = _useThemeContext2.localization;
  var _useTranslations2 = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations2.t;
  var breadcrumbLabel = t("Breadcrumb.defaultLabel");
  var _props$ariaLabel = props["aria-label"],
    ariaLabel = _props$ariaLabel === void 0 ? "".concat(breadcrumbLabel) : _props$ariaLabel,
    children = props.children,
    rest = _objectWithoutProperties(props, _excluded3);
  var listRef = React.useRef(null);
  var childCount = React.Children.count(children);
  var label = ariaLabel.toLowerCase().includes("breadcrumb") ? ariaLabel : "".concat(breadcrumbLabel, " ").concat(ariaLabel);
  function getCrumbs() {
    if (childCount < 5) {
      return React.Children.map(children, function (c, i) {
        return /*#__PURE__*/React.cloneElement(c, {
          isFirst: i === 0,
          isLast: i === React.Children.count(children) - 1
        });
      });
    }
    var collapsed = React.Children.toArray(children);
    var beforeCollapse = collapsed.shift();
    var afterCollapse = collapsed.splice(-2, 2);
    return [React.Children.map(beforeCollapse, function (c) {
      return /*#__PURE__*/React.cloneElement(c, {
        isFirst: true
      });
    })].concat(_toConsumableArray(collapsed.length > 0 ? [/*#__PURE__*/jsx(BreadcrumbItemsCollapsed, {
      children: collapsed
    }, "collapsed")] : []), [React.Children.map(afterCollapse, function (c, i) {
      return /*#__PURE__*/React.cloneElement(c, {
        isLast: i === afterCollapse.length - 1
      });
    })]);
  }
  return /*#__PURE__*/jsx(StyledBreadcrumb, _objectSpread(_objectSpread({
    "aria-label": label,
    ref: ref
  }, rest), {}, {
    children: /*#__PURE__*/jsx(StyledBreadcrumbList, {
      ref: listRef,
      children: getCrumbs()
    })
  }));
});
Breadcrumb.displayName = "Breadcrumb";

export { Breadcrumb };
